import { DocumentNode } from 'graphql';
import { PropsWithChildren, useMemo } from 'react';
import {
  AnyVariables,
  DocumentInput,
  Provider,
  TypedDocumentNode,
  useMutation,
  useSubscription,
  UseSubscriptionArgs,
  SubscriptionHandler,
  useQuery,
  UseQueryArgs,
} from 'urql';
import createGraphQLClient from 'studio5/clients/graphql';
import { useAuth } from './auth';
import { useWebSocket } from './websocket-api';

export default function GraphQLApiProvider({
  children,
}: PropsWithChildren<unknown>) {
  // eslint-disable-next-line @appcues/use-context-hooks-in-provider
  const { client } = useAuth();
  const { client: wsClient } = useWebSocket();

  const apiClient = useMemo(() => {
    return createGraphQLClient(client, wsClient);
  }, [client, wsClient]);

  return <Provider value={apiClient}>{children}</Provider>;
}

// We extend the urql useQuery hook to append a force-analytics request header
// when the FORCE_CLICKHOUSE or FORCE_SNOWFLAKE are enabled locally. This
// allows us to choose the analytics backend from studio for the purpose of testing.
// This should be removed once the clickhouse migration is complete.
export function useGraphQLQuery<
  QueryT,
  VariablesT extends AnyVariables = undefined
>(args: UseQueryArgs<VariablesT, QueryT>) {
  return useQuery<QueryT, VariablesT>({ ...args });
}

export function useGraphQLMutation<
  MutationT,
  VariablesT extends AnyVariables = undefined
>(
  query:
    | DocumentNode
    | TypedDocumentNode<MutationT, VariablesT>
    | string
    | DocumentInput<MutationT, VariablesT>
) {
  return useMutation<MutationT, VariablesT>(query);
}

export function useGraphQLSubscription<
  SubscriptionT,
  ResultT = SubscriptionT,
  VariablesT extends AnyVariables = undefined
>(
  args: UseSubscriptionArgs<VariablesT, SubscriptionT>,
  handler?: SubscriptionHandler<SubscriptionT, ResultT> | undefined
) {
  return useSubscription<SubscriptionT, ResultT, VariablesT>(args, handler);
}

export { useClient as useGraphQLClient } from 'urql';
